// handleLoginToken.js
'use strict';
import { push, replace } from '../action/navigationAction.js';
import fetchConfigurationsBootstrap from '../action/fetchConfigurationsBootstrap.js';
import addModal from '../action/addModal.js';
import removeModals from '../action/removeModals.js';
import getIsRemoteConfigMerged from '../selector/getIsRemoteConfigMerged.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { ManageProfileType } from '../resource/profileConstants.js';
import { CONFIG_PRIORITY_PRESENCE_USER } from '../resource/configPriority.js';
import { getIsIosSafari, getIsInPwa } from '../resource/getUserAgent.js';
import { login as loginModal } from '../resource/modalId.js';
import { FEATURE_PWA_DOWNLOAD_MODAL } from '../RemoteConfigKeys.js';
import getLoginData from '../selector/getLoginData.js';
import updateLoginData from '../action/updateLoginData.js';
import { getIsChinaPartner } from '../resource/partner.js';
import getRouterData from '../selector/getRouterData.js';
import getMeData from '../selector/getMeData.js';
import pushToastr from '../action/pushToastr.js';
import loginWithOAuth from '../action/loginWithOAuth.js';
import saveWelcomeBackData from '../action/saveWelcomeBackData.js';
import i18n from '../resource/i18n.js';
import { AuthId } from '../resource/loginRegisterConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { ButtonId, ModalId } from '../resource/mixpanel.js';

/**
 * Handle login token
 * @kind action
 * @param {string} {token} - action param.
 * @return {Promise} Action promise.
 */
const handleLoginToken =
  ({ token }) =>
  async (dispatch, getState) => {
    const isChinaPartner = getIsChinaPartner();
    const state = getState();
    dispatch(
      removeModals({
        ids: [
          loginModal.FORGOT_PASSWORD_PHONE_VERIFY,
          loginModal.FORGOT_PASSWORD_EMAIL_VERIFY,
          loginModal.PHONE_VERIFY,
          loginModal.EMAIL_VERIFY,
        ],
        transitionStatus: 'leaving',
      })
    );

    dispatch(
      removeModals({
        ids: [
          loginModal.METHOD_SELECT,
          loginModal.PASSWORD,
          loginModal.WELCOME_BACK,
        ],
        transitionStatus: 'leavingToBottom',
      })
    );

    dispatch(
      removeModals({
        ids: [loginModal.ACCOUNT_REGISTER],
        transitionStatus: 'reversing',
      })
    );

    await dispatch(
      fetchConfigurationsBootstrap({
        priority: CONFIG_PRIORITY_PRESENCE_USER,
      })
    );

    const isPresenceUserRemoteConfigMerged = getIsRemoteConfigMerged(
      getState(),
      CONFIG_PRIORITY_PRESENCE_USER
    );

    const shouldShowPwaSafariToast =
      getRemoteConfigData(getState(), FEATURE_PWA_DOWNLOAD_MODAL) === 1;

    if (
      getIsIosSafari() &&
      !getIsInPwa() &&
      shouldShowPwaSafariToast &&
      !isChinaPartner &&
      isPresenceUserRemoteConfigMerged
    ) {
      dispatch(addModal({ id: 'PwaSafariToast' }));
    }

    const from = getRouterData(state, ['location', 'state', 'from']);
    const pathname = getRouterData(state, ['location', 'pathname']);

    let target = '/';
    if (from) {
      target = `${from.pathname}${from.search}${from.hash}`;
    }
    if (['/login', '/signup'].includes(pathname)) {
      const searchParams = new URLSearchParams(
        getRouterData(state, ['location', 'search'])
      );
      const redirectUrl = searchParams.get('redirect_url');
      if (redirectUrl) {
        try {
          const url = new URL(redirectUrl);
          if ('https:' === url.protocol && /swag.live$/gi.test(url.hostname)) {
            url.searchParams.set('auth', token);
            location.replace(url.href);
          }
        } catch (error) {
          dispatch({ type: 'error', payload: error });
        }
      }
      dispatch(replace(target));
    } else {
      const [{ default: digestJwtToken }, { default: fetchFlightCheck }] =
        await Promise.all([
          import('./digestJwtToken.js'),
          import('./fetchFlightCheck.js'),
        ]);
      await dispatch(digestJwtToken({ token }));
      await dispatch(fetchFlightCheck({ token }));
      dispatch(saveWelcomeBackData());

      const entry = getLoginData(getState(), 'entry');
      if (entry === 'forgotPassword') {
        if (!/\/manage-profile/.test(location.pathname)) {
          dispatch(push(`/manage-profile/${ManageProfileType.ACCOUNT}`));
        }
        dispatch(
          addModal({ id: 'PasswordSetting', transitionStatus: 'coming' })
        );
        // Reset entry to avoid re-triggering this modal on next sign-in
        // (LOGOUT preserves login.entry).
        dispatch(updateLoginData({ dataKey: 'entry', value: null }));
      }
    }

    try {
      const ns = TranslationNamespace.NOTIFICATION;
      // Pre-trigger NOTIFICATION namespace translation fetch
      i18n.t('apple_rebind_notice_description', { ns });

      const { default: fetchAccountLinks } = await import(
        '../action/fetchAccountLinks.js'
      );
      await dispatch(fetchAccountLinks());
      const accountLinks = getMeData(getState(), 'accountLinks');

      if (
        accountLinks?.apple &&
        !accountLinks?.[AuthId.APPLE_ID_NEW] &&
        pathname !== '/apple-migration-rebind'
      ) {
        if (!i18n.customExists('apple_rebind_notice_description', { ns })) {
          await new Promise(resolve => {
            const handler = () => {
              i18n.store?.off('added', handler);
              clearTimeout(timer);
              resolve();
            };
            i18n.store?.on('added', handler);
            const timer = setTimeout(resolve, 3000);
          });
        }

        dispatch(
          pushToastr({
            textKey: 'apple_rebind_notice_title',
            textKeyOption: {
              ns,
            },
            content: i18n.t('apple_rebind_notice_description', {
              ns,
            }),
            type: 'info',
            trackingModalId: ModalId.AppleMigration.ModalAppleRebindNotice,
            primaryButton: {
              text: i18n.t('button_provider_rebind', {
                ns,
              }),
              trackingId: ButtonId.All.ButtonProviderRebind,
              onClick: () => {
                dispatch(
                  loginWithOAuth({
                    oAuth: AuthId.APPLE_ID_NEW,
                    isBinding: true,
                  })
                );
              },
            },
            secondaryButton: {
              text: i18n.t('general_cancel', {
                ns,
              }),
              trackingId: ButtonId.All.ButtonCancel,
              onClick: () => {},
            },
          })
        );
      }
    } catch {
      // silent fail
    }
  };

export default handleLoginToken;
